import { useState } from 'react'
import { Alert, Descriptions, Flex, Input, Tag } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { errorStatus, translateError } from '@/shared/api/errors'
import { formatDateTime } from '@/shared/lib/format'
import { fetchVoucherCard, type VoucherCardView } from './voucherApi'
import { parseCardRef } from './voucherPrintLayout'

interface VoucherCardLookupProps {
  planName: (planId: string) => string
}

/**
 * Kartani ustidagi `#<partiya>-<tartib raqami>` bo'yicha topish: holati,
 * kim sotgan va kim ishlatgan. Kod ko'rsatilmaydi va so'ralmaydi.
 */
export function VoucherCardLookup({ planName }: VoucherCardLookupProps) {
  const { t } = useTranslation()
  const [formatError, setFormatError] = useState(false)
  const lookup = useMutation({
    mutationFn: (ref: { batchNo: number; serialNo: number }) => fetchVoucherCard(ref.batchNo, ref.serialNo),
  })

  const search = (value: string): void => {
    lookup.reset()
    if (value.trim() === '') {
      setFormatError(false)
      return
    }
    const ref = parseCardRef(value)
    setFormatError(ref === null)
    if (ref) lookup.mutate(ref)
  }

  return (
    <Flex vertical gap={8} style={{ marginBottom: 16 }}>
      <Input.Search
        allowClear
        enterButton
        placeholder={t('voucher.lookup.placeholder')}
        aria-label={t('voucher.lookup.placeholder')}
        loading={lookup.isPending}
        onSearch={search}
        style={{ maxWidth: 360 }}
      />
      {formatError ? <Alert type="warning" showIcon message={t('voucher.lookup.invalid')} /> : null}
      {lookup.isError ? (
        <Alert
          type={errorStatus(lookup.error) === 404 ? 'warning' : 'error'}
          showIcon
          message={errorStatus(lookup.error) === 404 ? t('voucher.lookup.notFound') : translateError(lookup.error, t)}
        />
      ) : null}
      {lookup.data ? <CardDetails card={lookup.data} planName={planName(lookup.data.planId)} /> : null}
    </Flex>
  )
}

function CardDetails({ card, planName }: { card: VoucherCardView; planName: string }) {
  const { t } = useTranslation()
  const dash = t('common.dash')
  return (
    <Descriptions
      size="small"
      bordered
      column={{ xs: 1, md: 2 }}
      title={t('voucher.lookup.title', { batchNo: card.batchNo, serialNo: card.serialNo })}
      items={[
        { key: 'state', label: t('subscribers.columns.status'), children: <Tag>{t(`voucherState.${card.state}`)}</Tag> },
        { key: 'plan', label: t('voucher.batch.plan'), children: planName },
        { key: 'soldAt', label: t('voucher.soldAt'), children: card.soldAt ? formatDateTime(card.soldAt) : dash },
        { key: 'soldBy', label: t('voucher.soldBy'), children: card.soldByName ?? dash },
        {
          key: 'activatedAt',
          label: t('voucher.activatedAt'),
          children: card.activatedAt ? formatDateTime(card.activatedAt) : dash,
        },
        { key: 'activatedBy', label: t('voucher.activatedBy'), children: card.activatedByName ?? dash },
        { key: 'login', label: t('voucher.activateLogin'), children: card.subscriberLogin ?? dash },
        {
          key: 'expiresAt',
          label: t('voucher.lookup.expiresAt'),
          children: card.expiresAt ? formatDateTime(card.expiresAt) : dash,
        },
      ]}
    />
  )
}
